/**
 * TrustGrid Findings Page
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Spin, Select, Modal, Form, Input, DatePicker, Collapse, Typography, Checkbox, Alert } from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined, ThunderboltOutlined, UserOutlined, CheckCircleOutlined, BookOutlined, LinkOutlined, SafetyCertificateOutlined, ControlOutlined } from '@ant-design/icons';
import {
  listFindings,
  createFinding,
  updateFinding,
  deleteFinding,
  bulkCreateFindings,
  getFinding,
  updateFindingStatus,
  assignFinding,
  verifyFindingResolution,
  getFindingRemediation
} from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import FindingGovernanceModal from './FindingGovernanceModal';
import { showError, showSuccess } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;
const { Title, Paragraph, Text } = Typography;

function OldFindings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Old Findings', path: '/trustgrid/old-findings' }];
  }, [location.state]);

  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [remediationModalVisible, setRemediationModalVisible] = useState(false);
  const [remediationGuidance, setRemediationGuidance] = useState(null);
  const [loadingRemediation, setLoadingRemediation] = useState(false);
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [verificationChecklist, setVerificationChecklist] = useState([]);
  const [governanceModalVisible, setGovernanceModalVisible] = useState(false);
  const [governanceFindingId, setGovernanceFindingId] = useState(null);
  const [form] = Form.useForm();
  const [verificationForm] = Form.useForm();

  useEffect(() => {
    fetchFindings();
  }, [statusFilter]);

  const fetchFindings = async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const res = await listFindings(projectId, status);
      setFindings(res.data.findings || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Findings');
      if (!wasHandled) {
        console.error('Failed to fetch findings:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    setLoading(true);
    try {
      const res = await bulkCreateFindings(projectId);
      showSuccess(`Created ${res.data.count} findings from failing signals`);
      fetchFindings();
    } catch (error) {
      console.error('Failed to bulk create findings:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFinding = async (values) => {
    try {
      await createFinding(projectId, values);
      showSuccess('Finding created');
      setCreateModalVisible(false);
      form.resetFields();
      fetchFindings();
    } catch (error) {
      console.error('Failed to create finding:', error);
      showError(error);
    }
  };

  const handleUpdateStatus = async (findingId, newStatus) => {
    try {
      await updateFinding(findingId, { status: newStatus });
      showSuccess('Finding updated');
      fetchFindings();
    } catch (error) {
      console.error('Failed to update finding:', error);
      showError(error);
    }
  };

  const handleDelete = async (findingId) => {
    Modal.confirm({
      title: 'Delete Finding?',
      content: 'This action cannot be undone.',
      onOk: async () => {
        try {
          await deleteFinding(findingId);
          showSuccess('Finding deleted');
          fetchFindings();
        } catch (error) {
          console.error('Failed to delete finding:', error);
          showError(error);
        }
      }
    });
  };

  const handleViewDetail = async (finding) => {
    try {
      const res = await getFinding(finding.id);
      setSelectedFinding(res.data);
      setDetailModalVisible(true);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Findings');
      if (!wasHandled) {
        console.error('Failed to fetch finding details:', error);
        showError(error);
      }
    }
  };

  const handleStatusChange = async (findingId, newStatus) => {
    if (newStatus === 'resolved' || newStatus === 'risk_accepted') {
      Modal.confirm({
        title: `${newStatus === 'resolved' ? 'Resolve' : 'Accept Risk for'} Finding?`,
        content: (
          <Form id="resolutionForm" layout="vertical">
            <Form.Item label="Resolution Notes" name="resolution_notes" rules={[{ required: true, message: 'Resolution notes are required' }]}>
              <TextArea rows={4} placeholder="Describe the resolution or risk acceptance rationale" />
            </Form.Item>
          </Form>
        ),
        onOk: async () => {
          const formData = document.getElementById('resolutionForm');
          const notes = formData?.querySelector('textarea')?.value;
          if (!notes) {
            showError({ message: 'Resolution notes are required' });
            return;
          }
          try {
            await updateFindingStatus(findingId, newStatus, notes);
            showSuccess(`Finding ${newStatus}`);
            fetchFindings();
            setDetailModalVisible(false);
          } catch (error) {
            console.error('Failed to update finding status:', error);
            showError(error);
          }
        }
      });
    } else {
      try {
        await updateFindingStatus(findingId, newStatus);
        showSuccess(`Finding status updated to ${newStatus}`);
        fetchFindings();
        const res = await getFinding(findingId);
        setSelectedFinding(res.data);
      } catch (error) {
        console.error('Failed to update finding status:', error);
        showError(error);
      }
    }
  };

  const handleAssignment = async (findingId) => {
    Modal.confirm({
      title: 'Assign Finding',
      content: (
        <Form id="assignmentForm" layout="vertical">
          <Form.Item label="Assign to User ID" name="assigned_to">
            <Input type="number" placeholder="Enter user ID (leave empty to unassign)" />
          </Form.Item>
          <Form.Item label="Due Date" name="due_date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        const formData = document.getElementById('assignmentForm');
        const userId = formData?.querySelector('input[type="number"]')?.value;
        const dueDate = formData?.querySelector('input[placeholder*="date"]')?.value;
        try {
          await assignFinding(findingId, userId ? parseInt(userId) : null, dueDate || null);
          showSuccess('Finding assignment updated');
          fetchFindings();
          const res = await getFinding(findingId);
          setSelectedFinding(res.data);
        } catch (error) {
          console.error('Failed to assign finding:', error);
          showError(error);
        }
      }
    });
  };

  const handleVerification = (finding) => {
    // Initialize verification checklist
    const checklist = [
      { key: 'root_cause', label: 'Root cause has been identified and documented', checked: false },
      { key: 'fix_applied', label: 'Fix has been applied to production environment', checked: false },
      { key: 'tested', label: 'Fix has been tested and validated', checked: false },
      { key: 'no_regression', label: 'No regressions or side effects observed', checked: false },
      { key: 'documented', label: 'Changes are documented and reviewed', checked: false }
    ];
    setVerificationChecklist(checklist);
    setVerificationModalVisible(true);
  };

  const handleVerificationSubmit = async () => {
    try {
      const values = await verificationForm.validateFields();
      const allChecked = verificationChecklist.every(item => item.checked);

      if (!allChecked) {
        Modal.warning({
          title: 'Incomplete Verification',
          content: 'Please complete all checklist items before submitting verification.'
        });
        return;
      }

      await verifyFindingResolution(selectedFinding.id, values.verification_notes);
      showSuccess('Verification initiated - re-scan scheduled');
      setVerificationModalVisible(false);
      verificationForm.resetFields();
      fetchFindings();
      setDetailModalVisible(false);
    } catch (error) {
      if (error.errorFields) {
        // Validation error, do nothing (form will show errors)
        return;
      }
      console.error('Failed to verify finding:', error);
      showError(error);
    }
  };

  const handleChecklistChange = (key, checked) => {
    setVerificationChecklist(prev =>
      prev.map(item => item.key === key ? { ...item, checked } : item)
    );
  };

  const handleViewRemediation = async (findingId) => {
    setLoadingRemediation(true);
    try {
      const res = await getFindingRemediation(findingId);
      setRemediationGuidance(res.data);
      setRemediationModalVisible(true);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Findings');
      if (!wasHandled) {
        console.error('Failed to fetch remediation guidance:', error);
        showError(error);
      }
    } finally {
      setLoadingRemediation(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'gold';
      case 'low': return 'blue';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'red';
      case 'investigating': return 'orange';
      case 'resolved': return 'green';
      case 'risk_accepted': return 'purple';
      case 'false_positive': return 'gray';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: 'Control',
      dataIndex: 'control_code',
      key: 'control_code',
      width: 90,
      render: (code) => code ? <Tag color="blue">{code}</Tag> : '-'
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => handleViewDetail(record)}>{text}</a>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => (
        <Tag color={getSeverityColor(severity)}>{severity}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: 'Assigned',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      width: 100,
      render: (userId) => userId ? `User #${userId}` : '-'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title="View Details"
          />
          <Button
            size="small"
            type="primary"
            icon={<ControlOutlined />}
            onClick={() => {
              setGovernanceFindingId(record.id);
              setGovernanceModalVisible(true);
            }}
            title="Governance Actions"
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            title="Delete"
          />
        </div>
      )
    }
  ];

  return (
    <TrustGridPage
      title="Findings"
      subtitle="Track and manage security findings and violations"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchFindings}
      actions={
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Option value="all">All Status</Option>
            <Option value="open">Open</Option>
            <Option value="investigating">Investigating</Option>
            <Option value="resolved">Resolved</Option>
            <Option value="risk_accepted">Risk Accepted</Option>
            <Option value="false_positive">False Positive</Option>
          </Select>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleBulkCreate}
          >
            Generate from Signals
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            New Finding
          </Button>
        </div>
      }
    >
      <ProjectSelector />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : findings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          <p>No findings found</p>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleBulkCreate}
          >
            Generate Findings from Failing Signals
          </Button>
        </div>
      ) : (
        <Table
          dataSource={findings}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true
          }}
          bordered
        />
      )}

      {/* Create Finding Modal */}
      <Modal
        title="Create Finding"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateFinding}>
          <Form.Item name="signal_id" label="Signal ID" rules={[{ required: true }]}>
            <Input type="number" placeholder="Enter signal ID" />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Finding title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Describe the finding" />
          </Form.Item>
          <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
            <Select placeholder="Select severity">
              <Option value="critical">Critical</Option>
              <Option value="high">High</Option>
              <Option value="medium">Medium</Option>
              <Option value="low">Low</Option>
            </Select>
          </Form.Item>
          <Form.Item name="due_date" label="Due Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Create Finding
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={`Finding #${selectedFinding?.id}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedFinding && (
          <div>
            {/* Header Info */}
            <div style={{ marginBottom: 20 }}>
              <p><strong>Title:</strong> {selectedFinding.title}</p>
              <p><strong>Description:</strong> {selectedFinding.description || '-'}</p>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <Tag color={getSeverityColor(selectedFinding.severity)}>{selectedFinding.severity?.toUpperCase()}</Tag>
                <Tag color={getStatusColor(selectedFinding.status)}>{selectedFinding.status?.toUpperCase()}</Tag>
                {selectedFinding.control_code && <Tag color="blue">{selectedFinding.control_code}</Tag>}
              </div>
            </div>

            {/* Verification Status Alert */}
            {selectedFinding.verification_status === 'pending' && (
              <Alert
                message="Verification Pending"
                description="This finding has been marked for verification. A re-scan will be performed to confirm the resolution."
                type="info"
                showIcon
                icon={<SafetyCertificateOutlined />}
                style={{ marginBottom: 20 }}
              />
            )}
            {selectedFinding.verification_status === 'failed' && (
              <Alert
                message="Verification Failed"
                description="The verification scan detected that the issue persists. Please review the resolution and try again."
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}
            {selectedFinding.verification_status === 'passed' && (
              <Alert
                message="Verification Passed"
                description="The resolution has been verified successfully. The issue is no longer detected."
                type="success"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}

            {/* Workflow Actions */}
            <div style={{ marginBottom: 20, padding: 15, background: '#f5f5f5', borderRadius: 4 }}>
              <h4 style={{ marginTop: 0 }}>Workflow Actions</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {selectedFinding.status === 'open' && (
                  <Button
                    size="small"
                    onClick={() => handleStatusChange(selectedFinding.id, 'investigating')}
                  >
                    Start Investigation
                  </Button>
                )}
                {selectedFinding.status === 'investigating' && (
                  <>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleStatusChange(selectedFinding.id, 'resolved')}
                    >
                      Mark Resolved
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleStatusChange(selectedFinding.id, 'risk_accepted')}
                    >
                      Accept Risk
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleStatusChange(selectedFinding.id, 'false_positive')}
                    >
                      False Positive
                    </Button>
                  </>
                )}
                {(selectedFinding.status === 'resolved' || selectedFinding.status === 'risk_accepted') && (
                  <Button
                    size="small"
                    type="primary"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => handleVerification(selectedFinding)}
                  >
                    Verify Resolution
                  </Button>
                )}
                <Button
                  size="small"
                  icon={<UserOutlined />}
                  onClick={() => handleAssignment(selectedFinding.id)}
                >
                  {selectedFinding.assigned_to ? 'Reassign' : 'Assign'}
                </Button>
                <Button
                  size="small"
                  type="default"
                  icon={<BookOutlined />}
                  onClick={() => handleViewRemediation(selectedFinding.id)}
                  loading={loadingRemediation}
                >
                  View Remediation
                </Button>
              </div>
            </div>

            {/* Details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
              <p><strong>Signal ID:</strong> {selectedFinding.signal_id || '-'}</p>
              <p><strong>Alert ID:</strong> {selectedFinding.alert_id || '-'}</p>
              <p><strong>Case ID:</strong> {selectedFinding.case_id ? `#${selectedFinding.case_id}` : 'Not assigned'}</p>
              <p><strong>Assigned To:</strong> {selectedFinding.assigned_to ? `User #${selectedFinding.assigned_to}` : 'Unassigned'}</p>
              <p><strong>Due Date:</strong> {selectedFinding.due_date ? new Date(selectedFinding.due_date).toLocaleDateString() : '-'}</p>
              <p><strong>Created:</strong> {new Date(selectedFinding.created_at).toLocaleString()}</p>
            </div>

            {/* Workflow Timestamps */}
            {(selectedFinding.triaged_at || selectedFinding.resolved_at || selectedFinding.verified_at) && (
              <div style={{ marginTop: 20, padding: 15, background: '#f9f9f9', borderRadius: 4 }}>
                <h4 style={{ marginTop: 0 }}>Workflow History</h4>
                {selectedFinding.triaged_at && (
                  <p><strong>Triaged:</strong> {new Date(selectedFinding.triaged_at).toLocaleString()}</p>
                )}
                {selectedFinding.resolved_at && (
                  <p><strong>Resolved:</strong> {new Date(selectedFinding.resolved_at).toLocaleString()}</p>
                )}
                {selectedFinding.verified_at && (
                  <p>
                    <strong>Verified:</strong> {new Date(selectedFinding.verified_at).toLocaleString()}
                    {selectedFinding.verification_status && (
                      <Tag color={selectedFinding.verification_status === 'passed' ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                        {selectedFinding.verification_status}
                      </Tag>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Notes */}
            {selectedFinding.resolution_notes && (
              <div style={{ marginTop: 20 }}>
                <h4>Resolution Notes</h4>
                <p style={{ padding: 10, background: '#f9f9f9', borderRadius: 4 }}>
                  {selectedFinding.resolution_notes}
                </p>
              </div>
            )}
            {selectedFinding.verification_notes && (
              <div style={{ marginTop: 20 }}>
                <h4>Verification Notes</h4>
                <p style={{ padding: 10, background: '#f9f9f9', borderRadius: 4 }}>
                  {selectedFinding.verification_notes}
                </p>
              </div>
            )}

            {/* Control & Signal Info */}
            {(selectedFinding.signal || selectedFinding.control) && (
              <div style={{ marginTop: 20 }}>
                <h4>Related Information</h4>
                {selectedFinding.signal && (
                  <div>
                    <p><strong>Signal Status:</strong> <Tag>{selectedFinding.signal.status}</Tag></p>
                    <p><strong>Evidence Count:</strong> {selectedFinding.signal.evidence_count}</p>
                  </div>
                )}
                {selectedFinding.control && (
                  <div>
                    <p><strong>Control:</strong> {selectedFinding.control.code} - {selectedFinding.control.title}</p>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <Button onClick={() => setDetailModalVisible(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Remediation Guidance Modal */}
      <Modal
        title={
          <div>
            <BookOutlined style={{ marginRight: 8 }} />
            Remediation Guidance
          </div>
        }
        open={remediationModalVisible}
        onCancel={() => setRemediationModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRemediationModalVisible(false)}>
            Close
          </Button>
        ]}
        width={900}
      >
        {remediationGuidance && (
          <div>
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <Title level={4} style={{ marginTop: 0 }}>{remediationGuidance.title}</Title>
              <Paragraph>{remediationGuidance.overview}</Paragraph>
              <div style={{ display: 'flex', gap: 10 }}>
                <Tag color={getSeverityColor(remediationGuidance.severity)}>
                  {remediationGuidance.severity?.toUpperCase()}
                </Tag>
                <Tag color="blue">
                  <span style={{ fontSize: '12px' }}>⏱ {remediationGuidance.estimated_effort}</span>
                </Tag>
              </div>
            </div>

            {/* Finding Context */}
            {remediationGuidance.finding_context && (
              <div style={{ marginBottom: 20, padding: 15, background: '#f9f9f9', borderRadius: 4 }}>
                <Text strong>Context: </Text>
                <Text>Finding #{remediationGuidance.finding_context.id} - {remediationGuidance.finding_context.title}</Text>
              </div>
            )}

            {/* Remediation Steps */}
            {remediationGuidance.steps && remediationGuidance.steps.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <Title level={5}>Remediation Steps</Title>
                <Collapse
                  defaultActiveKey={['1']}
                  style={{ background: '#fff' }}
                >
                  {remediationGuidance.steps.map((step) => (
                    <Panel
                      header={
                        <span>
                          <Tag color="blue">Step {step.step}</Tag>
                          <Text strong>{step.title}</Text>
                        </span>
                      }
                      key={step.step}
                    >
                      <Paragraph>{step.description}</Paragraph>

                      {step.actions && step.actions.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <Text strong>Actions:</Text>
                          <ul style={{ marginTop: 5 }}>
                            {step.actions.map((action, idx) => (
                              <li key={idx}>{action}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {step.code_example && (
                        <div style={{ marginTop: 15 }}>
                          <Text strong>Code Example:</Text>
                          <pre style={{
                            marginTop: 8,
                            padding: 12,
                            background: '#f5f5f5',
                            borderRadius: 4,
                            border: '1px solid #d9d9d9',
                            overflow: 'auto',
                            fontSize: '12px',
                            lineHeight: '1.5'
                          }}>
                            {step.code_example}
                          </pre>
                        </div>
                      )}
                    </Panel>
                  ))}
                </Collapse>
              </div>
            )}

            {/* Best Practices */}
            {remediationGuidance.best_practices && remediationGuidance.best_practices.length > 0 && (
              <div style={{ marginBottom: 20, padding: 15, background: '#f0f9ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                <Title level={5} style={{ marginTop: 0 }}>Best Practices</Title>
                <ul style={{ marginBottom: 0 }}>
                  {remediationGuidance.best_practices.map((practice, idx) => (
                    <li key={idx}>{practice}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* References */}
            {remediationGuidance.references && remediationGuidance.references.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <Title level={5}>References</Title>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {remediationGuidance.references.map((ref, idx) => (
                    <div key={idx}>
                      <LinkOutlined style={{ marginRight: 8 }} />
                      <a href={ref.url} target="_blank" rel="noopener noreferrer">
                        {ref.title}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Verification Modal */}
      <Modal
        title={
          <div>
            <SafetyCertificateOutlined style={{ marginRight: 8 }} />
            Verify Finding Resolution
          </div>
        }
        open={verificationModalVisible}
        onOk={handleVerificationSubmit}
        onCancel={() => {
          setVerificationModalVisible(false);
          verificationForm.resetFields();
          setVerificationChecklist([]);
        }}
        okText="Submit Verification"
        width={700}
      >
        {selectedFinding && (
          <div>
            {/* Finding Context */}
            <div style={{ marginBottom: 20, padding: 15, background: '#f9f9f9', borderRadius: 4 }}>
              <Text strong>Finding: </Text>
              <Text>#{selectedFinding.id} - {selectedFinding.title}</Text>
              <div style={{ marginTop: 8 }}>
                <Tag color={getSeverityColor(selectedFinding.severity)}>{selectedFinding.severity?.toUpperCase()}</Tag>
                <Tag color={getStatusColor(selectedFinding.status)}>{selectedFinding.status?.toUpperCase()}</Tag>
                {selectedFinding.control_code && <Tag color="blue">{selectedFinding.control_code}</Tag>}
              </div>
            </div>

            {/* Alert Box */}
            <Alert
              message="Verification Process"
              description="Complete the checklist below to verify that the finding has been properly resolved. A re-scan will be scheduled to confirm the fix."
              type="info"
              showIcon
              style={{ marginBottom: 20 }}
            />

            {/* Verification Checklist */}
            <div style={{ marginBottom: 20 }}>
              <Title level={5}>Verification Checklist</Title>
              <div style={{ padding: 15, background: '#fafafa', borderRadius: 4 }}>
                {verificationChecklist.map((item) => (
                  <div key={item.key} style={{ marginBottom: 12 }}>
                    <Checkbox
                      checked={item.checked}
                      onChange={(e) => handleChecklistChange(item.key, e.target.checked)}
                    >
                      {item.label}
                    </Checkbox>
                  </div>
                ))}
              </div>
              <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
                All items must be checked to submit verification
              </Text>
            </div>

            {/* Verification Notes */}
            <Form form={verificationForm} layout="vertical">
              <Form.Item
                name="verification_notes"
                label="Verification Notes"
                rules={[{ required: true, message: 'Please provide verification notes' }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Describe the verification process, tests performed, and confirmation of resolution..."
                />
              </Form.Item>
            </Form>

            {/* Summary Info */}
            <div style={{ marginTop: 20, padding: 12, background: '#f0f9ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>What happens next?</Text>
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>Verification status will be set to "pending"</li>
                <li>A re-scan will be scheduled to confirm the fix</li>
                <li>If the issue is no longer detected, status will be "passed"</li>
                <li>If the issue persists, status will be "failed" and the finding will be reopened</li>
              </ul>
            </div>
          </div>
        )}
      </Modal>

      {/* Governance Actions Modal */}
      <FindingGovernanceModal
        visible={governanceModalVisible}
        findingId={governanceFindingId}
        onClose={() => setGovernanceModalVisible(false)}
        onSuccess={() => {
          fetchFindings();
        }}
      />
    </TrustGridPage>
  );
}

export default OldFindings;
